/**
 * Test microphone recorder.
 *
 * Owns the entire 10-second test-recording side flow: countdown timer,
 * MediaRecorder lifecycle, blob assembly, and the playback URL. Doesn't
 * touch the main recording state machine — `handleTestMic` is invoked from
 * the legacy panel and only cares about the audio destination stream.
 *
 * Returns the state needed by LegacyRecordingPanel plus the trigger.
 */
import { useCallback, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { RECORDER_AUDIO_BITS_PER_SECOND } from "../../../hooks/useAudioPipeline"

interface IProps {
  destinationRef: React.MutableRefObject<MediaStreamAudioDestinationNode | null>
  getMimeType: () => string | null
}

interface IUseTestMicRecorderResult {
  testRecording: boolean
  testCountdown: number
  testAudioUrl: string | null
  handleTestMic: () => void
}

export function useTestMicRecorder({ destinationRef, getMimeType }: IProps): IUseTestMicRecorderResult {
  const [testRecording, setTestRecording] = useState(false)
  const [testCountdown, setTestCountdown] = useState(0)
  const [testAudioUrl, setTestAudioUrl] = useState<string | null>(null)
  const testRecorderRef = useRef<MediaRecorder | null>(null)

  const handleTestMic = useCallback((): void => {
    if (testRecording) return
    const stream = destinationRef.current?.stream
    if (!stream) return

    if (testAudioUrl) URL.revokeObjectURL(testAudioUrl)

    flushSync(() => {
      setTestAudioUrl(null)
      setTestRecording(true)
      setTestCountdown(10)
    })

    const mimeType = getMimeType() ?? "audio/webm"
    const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: RECORDER_AUDIO_BITS_PER_SECOND })
    const chunks: Blob[] = []

    recorder.ondataavailable = (e): void => {
      if (e.data.size > 0) chunks.push(e.data)
    }
    recorder.onstop = (): void => {
      const blob = new Blob(chunks, { type: mimeType })
      setTestAudioUrl(URL.createObjectURL(blob))
      setTestRecording(false)
      setTestCountdown(0)
    }
    recorder.onerror = (): void => {
      setTestRecording(false)
      setTestCountdown(0)
    }

    testRecorderRef.current = recorder
    recorder.start(1000)

    let sec = 10
    const timer = setInterval(() => {
      sec--
      setTestCountdown(sec)
      if (sec <= 0) {
        clearInterval(timer)
        recorder.stop()
      }
    }, 1000)
  }, [testRecording, testAudioUrl, getMimeType, destinationRef])

  return { testRecording, testCountdown, testAudioUrl, handleTestMic }
}
